import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { useTranslation } from '@tyro/i18n';
import { useMemo } from 'react';
import {
  type ReturnTypeFromUseDocumentationPages,
  useDocumentationPages,
} from '../../api/pages';
import { useReaderActions } from '../../store/documentation-reader-provider';
import { SectionLabel } from './section-label';
import { SubPageCard } from './sub-page-card';

interface ReaderSubPagesSectionProps {
  pageId: number;
  spaceId: number;
}

export function ReaderSubPagesSection({
  pageId,
  spaceId,
}: ReaderSubPagesSectionProps) {
  const { t } = useTranslation(['documentation']);
  const { navigate } = useReaderActions();
  const { data: allPages } = useDocumentationPages(spaceId);

  const directChildren = useMemo<ReturnTypeFromUseDocumentationPages[]>(() => {
    if (!allPages) return [];
    return allPages
      .filter(
        (candidate) =>
          candidate.parentPageId === pageId &&
          candidate.publishedVersionId != null,
      )
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [pageId, allPages]);

  const grandchildCounts = useMemo(() => {
    const counts = new Map<number, number>();
    if (!allPages) return counts;
    for (const candidate of allPages) {
      if (
        candidate.parentPageId != null &&
        candidate.publishedVersionId != null
      ) {
        counts.set(
          candidate.parentPageId,
          (counts.get(candidate.parentPageId) ?? 0) + 1,
        );
      }
    }
    return counts;
  }, [allPages]);

  if (directChildren.length === 0) return null;

  return (
    <Stack sx={{ mt: 4, gap: 1.5 }}>
      <Stack direction="row" alignItems="center" gap={1}>
        <SectionLabel>{t('documentation:reader.subPagesHeader')}</SectionLabel>
        <Chip
          label={directChildren.length}
          size="small"
          sx={{
            height: 20,
            minWidth: 24,
            fontSize: 11,
            fontWeight: 600,
            bgcolor: 'slate.100',
            color: 'text.disabled',
          }}
        />
      </Stack>
      <Grid container spacing={1.5}>
        {directChildren.map((child) => (
          <Grid key={child.id} size={{ xs: 6, sm: 4, xl: 3 }}>
            <SubPageCard
              child={child}
              grandchildCount={grandchildCounts.get(child.id) ?? 0}
              onSelect={() =>
                navigate({
                  view: 'page',
                  spaceId,
                  pageSlug: child.slug,
                })
              }
            />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}
